/**
 * Admin Funnel Panel
 * ───────────────────
 * • Top: 3 funnel stats cards (leads, video_play_rate, conversion, revenue)
 * • Creatives library per funnel with 3 kinds (image / short_anim / long_anim)
 * • Activate / delete / AI-generate more variants
 * • Active creative shown with gold border + Star badge
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Users, TrendingUp, Euro, PlayCircle, Image as ImageIcon, Film,
  Zap, Check, Trash2, Sparkles, Loader2, Star, Copy, Link as LinkIcon,
  Trophy, Pause, Scale, Globe, Wand2, Eraser, Scissors, RefreshCw,
  Palette, Layers,
} from "lucide-react";

import FunnelCreativePreview from "./FunnelCreativePreview";
import CanvasEditorModal from "./CanvasEditorModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country code → flag emoji + label. Covers our Phase-1 + Phase-2 markets.
const COUNTRIES = {
  SK: { flag: "🇸🇰", label: "Slovensko" },
  CZ: { flag: "🇨🇿", label: "Česko" },
  US: { flag: "🇺🇸", label: "USA" },
  DE: { flag: "🇩🇪", label: "Nemecko" },
  AT: { flag: "🇦🇹", label: "Rakúsko" },
  PL: { flag: "🇵🇱", label: "Poľsko" },
  HU: { flag: "🇭🇺", label: "Maďarsko" },
  FR: { flag: "🇫🇷", label: "Francúzsko" },
  ES: { flag: "🇪🇸", label: "Španielsko" },
  IT: { flag: "🇮🇹", label: "Taliansko" },
  PT: { flag: "🇵🇹", label: "Portugalsko" },
  NL: { flag: "🇳🇱", label: "Holandsko" },
  GB: { flag: "🇬🇧", label: "UK" },
  IE: { flag: "🇮🇪", label: "Írsko" },
  CH: { flag: "🇨🇭", label: "Švajčiarsko" },
  SE: { flag: "🇸🇪", label: "Švédsko" },
  NO: { flag: "🇳🇴", label: "Nórsko" },
  DK: { flag: "🇩🇰", label: "Dánsko" },
  FI: { flag: "🇫🇮", label: "Fínsko" },
  RO: { flag: "🇷🇴", label: "Rumunsko" },
  BG: { flag: "🇧🇬", label: "Bulharsko" },
  HR: { flag: "🇭🇷", label: "Chorvátsko" },
  UA: { flag: "🇺🇦", label: "Ukrajina" },
};
function countryLabel(cc) {
  const m = COUNTRIES[cc];
  return m ? `${m.flag} ${m.label}` : `${cc === "—" ? "🌍" : "🏳️"} ${cc}`;
}

const KIND_META = {
  image: { label: "Image", Icon: ImageIcon, color: "#60a5fa" },
  short_anim: { label: "Short animation (6-8s)", Icon: Zap, color: "#f472b6" },
  long_anim: { label: "Long animation (15-20s)", Icon: Film, color: "#a78bfa" },
};

function adminHeaders() {
  const email = JSON.parse(localStorage.getItem("user") || "{}").email || "admin@toprankpulse.com";
  return { "X-User-Email": email };
}

export default function AdminFunnelTab() {
  const [stats, setStats] = useState({ funnels: [], by_country: [] });
  const [creatives, setCreatives] = useState({ by_funnel: [] });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyGen, setBusyGen] = useState(null);
  const [busyEval, setBusyEval] = useState(false);
  const [copyOk, setCopyOk] = useState(null);
  const [countryFilter, setCountryFilter] = useState("ALL");
  const [canvasCreative, setCanvasCreative] = useState(null);
  const [busyBatch, setBusyBatch] = useState(false);
  const [batchLimit, setBatchLimit] = useState(5);
  const [batchProgress, setBatchProgress] = useState(null); // {done, total} or null

  async function refresh(country = countryFilter) {
    setLoading(true);
    try {
      const statsUrl = country && country !== "ALL"
        ? `${API}/funnel/admin/stats?country=${country}`
        : `${API}/funnel/admin/stats`;
      const [s, c, ev] = await Promise.all([
        axios.get(statsUrl, { headers: adminHeaders() }),
        axios.get(`${API}/funnel/admin/creatives`, { headers: adminHeaders() }),
        axios.get(`${API}/funnel/admin/autopause/events?limit=5`, { headers: adminHeaders() }).catch(() => ({ data: { events: [] } })),
      ]);
      setStats(s.data);
      setCreatives(c.data);
      setEvents(ev.data.events || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  function applyCountry(cc) {
    setCountryFilter(cc);
    refresh(cc);
  }

  async function evaluateWinners() {
    setBusyEval(true);
    try {
      const { data } = await axios.post(`${API}/funnel/admin/autopause/evaluate`, null, { headers: adminHeaders() });
      await refresh();
      window.alert(data.count > 0
        ? `Auto-winner spustený pre ${data.count} skupín`
        : "Žiadny auto-winner zatiaľ (potrebných ≥100 impressions + ≥3 leadov + 20% margin)");
    } finally {
      setBusyEval(false);
    }
  }

  async function activate(cid) {
    await axios.post(`${API}/funnel/admin/creatives/${cid}/activate`, null, { headers: adminHeaders() });
    refresh();
  }
  async function remove(cid) {
    if (!window.confirm("Zmazať túto reklamu?")) return;
    await axios.delete(`${API}/funnel/admin/creatives/${cid}`, { headers: adminHeaders() });
    refresh();
  }
  async function generate(slug, kind, style = "default") {
    const key = `${slug}-${kind}-${style}`;
    setBusyGen(key);
    try {
      await axios.post(
        `${API}/funnel/admin/creatives/generate`,
        { funnel_slug: slug, kind, count: 3, language: "sk", style },
        { headers: adminHeaders() },
      );
      refresh();
    } finally {
      setBusyGen(null);
    }
  }

  async function saveMediaUrl(cid, newUrl) {
    await axios.patch(
      `${API}/funnel/admin/creatives/${cid}`,
      { media_url: newUrl || null },
      { headers: adminHeaders() },
    );
    refresh();
  }

  const [busyImg, setBusyImg] = useState(null);
  async function generateImage(cid) {
    setBusyImg(cid);
    try {
      const { data } = await axios.post(
        `${API}/funnel/admin/creatives/${cid}/generate-image`,
        null,
        { headers: adminHeaders() },
      );
      await refresh();
      if (!data?.url) window.alert("Image generation returned no URL");
    } catch (e) {
      window.alert(`Generovanie obrázku zlyhalo: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyImg(null);
    }
  }

  async function reapplyOverlay(cid) {
    setBusyImg(cid);
    try {
      await axios.post(
        `${API}/funnel/admin/creatives/${cid}/reapply-overlay`,
        {}, { headers: adminHeaders() },
      );
      await refresh();
    } catch (e) {
      window.alert(`Overlay zlyhalo: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyImg(null);
    }
  }

  async function batchGenerateMissing(funnel_slug = null, kind = null) {
    // Split user-picked limit into chunks of 3 so the admin sees live
    // progress ("X / Y done") instead of a frozen spinner for 2-5 minutes.
    // Each chunk is a separate backend round-trip → stays well under ingress
    // timeouts (≤75s per chunk vs ~25s × 3 ≈ 75s worst case).
    const total = Math.max(1, Math.min(Number(batchLimit) || 5, 20));
    const chunk = 3;
    setBusyBatch(true);
    setBatchProgress({ done: 0, total, generated: 0, errors: 0 });
    let generated = 0, errors = 0, lastEligible = null;
    try {
      for (let done = 0; done < total; done += chunk) {
        const step = Math.min(chunk, total - done);
        const { data } = await axios.post(
          `${API}/funnel/admin/creatives/batch-generate-images`,
          { funnel_slug, kind, limit: step },
          { headers: adminHeaders(), timeout: 180000 },
        );
        generated += (data.generated || []).length;
        errors += (data.errors || []).length;
        lastEligible = data.total_eligible ?? 0;
        setBatchProgress({ done: done + step, total, generated, errors });
        // No more eligible creatives → stop early.
        if ((data.total_eligible ?? 0) === 0) break;
      }
      await refresh();
      window.alert(
        generated === 0 && lastEligible === 0
          ? "Žiadne chýbajúce obrázky — všetky kreatívy už majú vygenerovaný raw obrázok."
          : `Batch dokončený:\n✓ ${generated} vygenerovaných\n✗ ${errors} chýb`,
      );
    } catch (e) {
      window.alert(`Batch zlyhal: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyBatch(false);
      setBatchProgress(null);
    }
  }

  async function editImage(cid, operation) {
    let extra = null;
    if (operation === "magic_eraser") {
      extra = window.prompt("Čo chceš odstrániť z obrázku? (popíš objekt anglicky alebo slovensky)");
      if (!extra) return;
    } else if (operation === "replace_bg") {
      extra = window.prompt("Aké pozadie chceš? (napr. 'modern office with city skyline at sunset')");
      if (!extra) return;
    }
    setBusyImg(cid);
    try {
      await axios.post(
        `${API}/funnel/admin/creatives/${cid}/edit-image`,
        { operation, extra_prompt: extra },
        { headers: adminHeaders() },
      );
      await refresh();
    } catch (e) {
      window.alert(`Úprava zlyhala: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyImg(null);
    }
  }

  async function copyAdText(c) {
    const block = [
      `PRODUKT: ${c.funnel_slug}`,
      `ANGLE: ${c.note || "—"}`,
      ``,
      `HEADLINE: ${c.headline}`,
      ``,
      `PRIMARY TEXT:`,
      c.primary_text,
      ``,
      `CTA: ${c.cta}`,
      c.media_url ? `\nMEDIA: ${c.media_url}` : "",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(block);
      setCopyOk(c.id);
      setTimeout(() => setCopyOk(null), 1500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = block; document.body.appendChild(ta);
      ta.select(); document.execCommand("copy"); ta.remove();
      setCopyOk(c.id);
      setTimeout(() => setCopyOk(null), 1500);
    }
  }

  if (loading) {
    return (
      <div data-testid="admin-funnel-panel" style={{ padding: 40, textAlign: "center" }}>
        <Loader2 className="animate-spin inline-block" /> <span style={{ marginLeft: 10 }}>Načítavam Funnel stats...</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-funnel-panel" className="space-y-6">
      {/* Country filter + global country breakdown */}
      <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="funnel-country-filter">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Globe size={16} className="text-gray-500" />
          <h4 className="text-xs font-black text-gray-700 uppercase tracking-wide">Štáty</h4>
          <div className="flex items-center gap-1.5 flex-wrap ml-2">
            <button
              onClick={() => applyCountry("ALL")}
              data-testid="funnel-country-ALL"
              className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors ${countryFilter === "ALL" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              🌍 Všetky
            </button>
            {(stats.by_country || []).slice(0, 8).map((c) => (
              <button
                key={c.country}
                onClick={() => applyCountry(c.country)}
                data-testid={`funnel-country-${c.country}`}
                className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors ${countryFilter === c.country ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
              >
                {countryLabel(c.country)} · {c.leads}
              </button>
            ))}
          </div>
        </div>
        {(stats.by_country || []).length === 0 ? (
          <p className="text-xs text-gray-400">Zatiaľ žiadne leady. Akonáhle prvý lead pristane, uvidíš tu rozdelenie podľa štátu.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 text-left">
                  <th className="py-1.5 pr-3 font-bold">Štát</th>
                  <th className="py-1.5 pr-3 font-bold">Leady</th>
                  <th className="py-1.5 pr-3 font-bold">Kúpy</th>
                  <th className="py-1.5 pr-3 font-bold">CR</th>
                  <th className="py-1.5 pr-3 font-bold">Tržby</th>
                </tr>
              </thead>
              <tbody>
                {stats.by_country.map((c) => (
                  <tr key={c.country} className="border-t border-gray-100">
                    <td className="py-1.5 pr-3 font-semibold">{countryLabel(c.country)}</td>
                    <td className="py-1.5 pr-3 font-mono">{c.leads}</td>
                    <td className="py-1.5 pr-3 font-mono">{c.purchases}</td>
                    <td className="py-1.5 pr-3 font-mono">{c.conversion_rate}%</td>
                    <td className="py-1.5 pr-3 font-mono text-emerald-600">€{c.revenue_eur}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Winner auto-pause toolbar */}
      <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
            <Trophy size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-sm font-black text-gray-900">Winner Auto-Pause</h3>
            <p className="text-xs text-gray-600">
              Automaticky paušuje slabšie reklamy keď iná v skupine má ≥20 % vyšší lead-rate. Min 100 impr. + 3 leady, 12h cooldown.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex items-center gap-1.5 bg-white/70 border border-indigo-200 rounded-lg px-2 py-1" data-testid="funnel-batch-controls">
            <label className="text-[10px] font-black text-gray-500 uppercase tracking-wider">Limit</label>
            <input
              type="number"
              min="1" max="20"
              value={batchLimit}
              onChange={(e) => setBatchLimit(e.target.value)}
              disabled={busyBatch}
              data-testid="funnel-batch-limit-input"
              className="w-14 text-sm font-bold text-center bg-transparent border-0 focus:outline-none disabled:opacity-60"
            />
          </div>
          <button
            onClick={() => batchGenerateMissing()}
            disabled={busyBatch}
            data-testid="funnel-batch-generate-all-btn"
            title={`Vygeneruj obrázky pre max ${batchLimit} kreatív (po 3 v chunku — 25s / chunk)`}
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-60"
          >
            {busyBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
            {busyBatch && batchProgress
              ? `${batchProgress.done} / ${batchProgress.total}`
              : "Batch Generate Missing"}
          </button>
          <button
            onClick={evaluateWinners}
            disabled={busyEval}
            data-testid="funnel-evaluate-winners-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-60"
          >
            {busyEval ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
            Vyhodnotiť teraz
          </button>
        </div>
      </div>

      {events.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="funnel-autopause-events">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={16} className="text-amber-500" />
            <h4 className="text-xs font-black text-gray-700 uppercase tracking-wide">Posledné auto-winner udalosti</h4>
          </div>
          <div className="space-y-1.5">
            {events.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-gray-700">
                <span className="font-mono text-gray-400">{new Date(e.fired_at).toLocaleString("sk-SK")}</span>
                <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded font-bold">{e.funnel_slug} / {e.kind}</span>
                <span className="font-semibold truncate flex-1">{e.winner_headline}</span>
                <span className="text-green-600 font-bold">+{e.margin_pct}%</span>
                <span className="text-gray-400">({e.winner_rate}% vs {e.runner_rate}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(stats.funnels || []).map((f) => (
          <div key={f.funnel_slug} className="rounded-xl border border-gray-200 bg-white p-5" data-testid={`funnel-stat-${f.funnel_slug}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-black text-gray-900">{f.funnel_title}</h3>
              <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                €{f.price_eur}{f.funnel_slug === "autopilot" ? "/mes" : ""}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Stat icon={Users} label="Leads" val={f.leads} tint="#3b82f6" />
              <Stat icon={PlayCircle} label="Video %" val={`${f.video_play_rate}%`} tint="#f59e0b" />
              <Stat icon={TrendingUp} label="Konverzia" val={`${f.conversion_rate}%`} tint="#10b981" />
              <Stat icon={Euro} label="Tržby" val={`€${f.revenue_eur}`} tint="#a855f7" />
            </div>
            {(f.by_country || []).length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`funnel-by-country-${f.funnel_slug}`}>
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1.5">Podľa štátu</p>
                <div className="space-y-1">
                  {f.by_country.slice(0, 5).map((c) => (
                    <div key={c.country} className="flex items-center justify-between text-[11px]">
                      <span className="font-semibold">{countryLabel(c.country)}</span>
                      <span className="flex items-center gap-2 font-mono text-gray-600">
                        <span>{c.leads} lead</span>
                        {c.purchases > 0 && (
                          <span className="text-emerald-600 font-bold">· {c.purchases}× €{c.revenue_eur}</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <FunnelVideoSettings slug={f.funnel_slug} />
          </div>
        ))}
      </div>

      {/* Creatives library */}
      {(creatives.by_funnel || []).map((g) => (
        <div key={g.funnel_slug} className="rounded-xl border border-gray-200 bg-white p-5" data-testid={`funnel-creatives-${g.funnel_slug}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-black text-gray-900">{g.funnel_title}</h3>
              <p className="text-xs text-gray-500">URL: /system/{g.funnel_slug}</p>
            </div>
            <button
              onClick={() => batchGenerateMissing(g.funnel_slug)}
              disabled={busyBatch}
              data-testid={`funnel-batch-gen-${g.funnel_slug}`}
              title="Vygeneruj obrázky len pre tento funnel"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 disabled:opacity-60"
            >
              {busyBatch ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
              Batch
            </button>
          </div>

          {/* 3 columns — one per kind */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {["image", "short_anim", "long_anim"].map((kind) => {
              const meta = KIND_META[kind];
              const KindIcon = meta.Icon;
              const items = g.by_kind[kind] || [];
              const activeId = g.active[kind];

              return (
                <div key={kind} className="border border-gray-100 rounded-lg bg-gray-50 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: meta.color }}>
                      <KindIcon size={16} />
                      {meta.label}
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      <button
                        onClick={() => generate(g.funnel_slug, kind, "default")}
                        disabled={busyGen === `${g.funnel_slug}-${kind}-default`}
                        data-testid={`funnel-gen-${g.funnel_slug}-${kind}`}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-bold bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-60"
                        title="Default — krátka direct-response copy"
                      >
                        {busyGen === `${g.funnel_slug}-${kind}-default` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        AI +3
                      </button>
                      <button
                        onClick={() => generate(g.funnel_slug, kind, "sk_dr_bartek")}
                        disabled={busyGen === `${g.funnel_slug}-${kind}-sk_dr_bartek`}
                        data-testid={`funnel-gen-bartek-${g.funnel_slug}-${kind}`}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-bold bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded hover:opacity-90 disabled:opacity-60"
                        title="SK long-form contrarian (overený CZ/SK FB)"
                      >
                        {busyGen === `${g.funnel_slug}-${kind}-sk_dr_bartek` ? <Loader2 className="w-3 h-3 animate-spin" /> : "🔥"}
                        SK+3
                      </button>
                      <button
                        onClick={() => generate(g.funnel_slug, kind, "infographic_hero")}
                        disabled={busyGen === `${g.funnel_slug}-${kind}-infographic_hero`}
                        data-testid={`funnel-gen-info-${g.funnel_slug}-${kind}`}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-bold bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded hover:opacity-90 disabled:opacity-60"
                        title="50+ tasks infografika (Jobescape štýl)"
                      >
                        {busyGen === `${g.funnel_slug}-${kind}-infographic_hero` ? <Loader2 className="w-3 h-3 animate-spin" /> : "📊"}
                        INFO+3
                      </button>
                      <button
                        onClick={() => generate(g.funnel_slug, kind, "sk_founder_brand")}
                        disabled={busyGen === `${g.funnel_slug}-${kind}-sk_founder_brand`}
                        data-testid={`funnel-gen-founder-${g.funnel_slug}-${kind}`}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-bold bg-gradient-to-r from-red-500 to-orange-600 text-white rounded hover:opacity-90 disabled:opacity-60"
                        title="Founder brand ad (money-rain Konverzkar štýl)"
                      >
                        {busyGen === `${g.funnel_slug}-${kind}-sk_founder_brand` ? <Loader2 className="w-3 h-3 animate-spin" /> : "👤"}
                        FOUNDER+3
                      </button>
                      <button
                        onClick={() => generate(g.funnel_slug, kind, "comparison_infographic")}
                        disabled={busyGen === `${g.funnel_slug}-${kind}-comparison_infographic`}
                        data-testid={`funnel-gen-compare-${g.funnel_slug}-${kind}`}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-bold bg-gradient-to-r from-orange-500 to-purple-600 text-white rounded hover:opacity-90 disabled:opacity-60"
                        title="Comparison infographic — side-by-side metriky (Shopping in EU štýl)"
                      >
                        {busyGen === `${g.funnel_slug}-${kind}-comparison_infographic` ? <Loader2 className="w-3 h-3 animate-spin" /> : "⚖️"}
                        COMPARE+3
                      </button>
                    </div>
                  </div>

                  {items.length === 0 ? (
                    <p className="text-xs text-gray-400 py-4 text-center">Žiadne reklamy. Klikni AI +3.</p>
                  ) : (
                    <div className="space-y-2">
                      {items.map((c) => {
                        const isActive = c.id === activeId;
                        const isPaused = !!c.is_paused;
                        const isAutoWinner = !!c.auto_winner_at && isActive;
                        const borderClass = isAutoWinner
                          ? "bg-white border-2 border-amber-500 ring-2 ring-amber-200"
                          : isActive
                            ? "bg-white border-2 border-amber-400"
                            : isPaused
                              ? "bg-gray-50 border border-gray-200 opacity-75"
                              : "bg-white border border-gray-200";
                        return (
                          <div
                            key={c.id}
                            className={`rounded-md p-2.5 text-xs ${borderClass}`}
                            data-testid={`funnel-creative-${c.id}`}
                          >
                            {/* Preview at top */}
                            <div className="mb-2 flex justify-center">
                              <FunnelCreativePreview creative={c} />
                            </div>
                            <div className="flex items-start justify-between gap-2">
                              <p className="font-bold text-gray-900 flex-1">{c.headline}</p>
                              {isAutoWinner && (
                                <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded text-[9px] font-black uppercase tracking-wide flex-shrink-0"
                                      data-testid={`funnel-auto-winner-${c.id}`}
                                      title="Automaticky vybraný víťaz algoritmom">
                                  <Trophy size={9} /> Auto winner
                                </span>
                              )}
                              {isActive && !isAutoWinner && <Star size={14} className="text-amber-500 flex-shrink-0" fill="currentColor" />}
                              {isPaused && (
                                <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded text-[9px] font-black uppercase tracking-wide flex-shrink-0"
                                      data-testid={`funnel-paused-${c.id}`}
                                      title={c.pause_reason === "auto_winner" ? "Pauznuté auto-pause algoritmom" : "Pauznuté"}>
                                  <Pause size={9} /> Pauznuté
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 text-[11px] mt-1 line-clamp-3">{c.primary_text}</p>
                            {c.image_brief && (
                              <div className="mt-2 p-1.5 bg-indigo-50 border border-indigo-200 rounded text-[10px] text-indigo-900" data-testid={`funnel-image-brief-${c.id}`}>
                                <div className="flex items-center justify-between mb-0.5 gap-2">
                                  <span className="font-black uppercase tracking-wider text-indigo-700">Image brief</span>
                                  <button
                                    onClick={() => generateImage(c.id)}
                                    disabled={busyImg === c.id}
                                    data-testid={`funnel-image-gen-${c.id}`}
                                    className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-600 text-white rounded hover:opacity-90 disabled:opacity-60"
                                    title="Vygenerovať obrázok cez Gemini Nano Banana"
                                  >
                                    {busyImg === c.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Wand2 className="w-2.5 h-2.5" />}
                                    {c.media_url ? "REGEN" : "GEN"}
                                  </button>
                                </div>
                                <span className="line-clamp-3">{c.image_brief}</span>
                              </div>
                            )}
                            {c.raw_filename && (
                              <div className="mt-1.5 flex gap-1 flex-wrap" data-testid={`funnel-edit-toolbar-${c.id}`}>
                                <button
                                  onClick={() => reapplyOverlay(c.id)}
                                  disabled={busyImg === c.id}
                                  title="Re-apply text overlay (1s) — po úprave headline/CTA"
                                  className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-60"
                                  data-testid={`funnel-reoverlay-${c.id}`}
                                >
                                  <RefreshCw size={10} /> Overlay
                                </button>
                                <button
                                  onClick={() => editImage(c.id, "remove_bg")}
                                  disabled={busyImg === c.id}
                                  title="Odstrániť pozadie (Gemini edit)"
                                  className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold bg-teal-100 text-teal-700 rounded hover:bg-teal-200 disabled:opacity-60"
                                  data-testid={`funnel-remove-bg-${c.id}`}
                                >
                                  <Scissors size={10} /> BG-
                                </button>
                                <button
                                  onClick={() => editImage(c.id, "magic_eraser")}
                                  disabled={busyImg === c.id}
                                  title="Magická guma — odstrániť objekt"
                                  className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold bg-rose-100 text-rose-700 rounded hover:bg-rose-200 disabled:opacity-60"
                                  data-testid={`funnel-magic-eraser-${c.id}`}
                                >
                                  <Eraser size={10} /> Erase
                                </button>
                                <button
                                  onClick={() => editImage(c.id, "replace_bg")}
                                  disabled={busyImg === c.id}
                                  title="Vymeniť pozadie — opíš nové"
                                  className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-700 rounded hover:bg-violet-200 disabled:opacity-60"
                                  data-testid={`funnel-replace-bg-${c.id}`}
                                >
                                  <Wand2 size={10} /> BG+
                                </button>
                                <button
                                  onClick={() => setCanvasCreative(c)}
                                  disabled={busyImg === c.id}
                                  title="Canvas editor — farby, pozícia, badge, text"
                                  className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded hover:opacity-90 disabled:opacity-60"
                                  data-testid={`funnel-canvas-${c.id}`}
                                >
                                  <Palette size={10} /> Canvas
                                </button>
                              </div>
                            )}
                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-800 rounded text-[10px] font-bold">{c.cta}</span>
                              {c.note && <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px]">{c.note}</span>}
                              {c.style === "sk_dr_bartek" && (
                                <span className="px-1.5 py-0.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded text-[10px] font-bold">🔥 SK</span>
                              )}
                              {c.style === "infographic_hero" && (
                                <span className="px-1.5 py-0.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded text-[10px] font-bold">📊 INFO</span>
                              )}
                              {c.style === "sk_founder_brand" && (
                                <span className="px-1.5 py-0.5 bg-gradient-to-r from-red-500 to-orange-600 text-white rounded text-[10px] font-bold">👤 FOUNDER</span>
                              )}
                              {c.style === "comparison_infographic" && (
                                <span className="px-1.5 py-0.5 bg-gradient-to-r from-orange-500 to-purple-600 text-white rounded text-[10px] font-bold">⚖️ COMPARE</span>
                              )}
                              {c.stats && (c.stats.leads > 0 || c.stats.impressions > 0) && (
                                <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px]">
                                  {c.stats.impressions || 0} imp · {c.stats.leads || 0} lead
                                </span>
                              )}
                            </div>
                            {c.kind === "image" && (
                              <ImageUrlInput
                                initial={c.media_url || ""}
                                onSave={(u) => saveMediaUrl(c.id, u)}
                                testId={`funnel-img-url-${c.id}`}
                              />
                            )}
                            <div className="flex gap-1 mt-2">
                              {!isActive ? (
                                <button
                                  onClick={() => activate(c.id)}
                                  data-testid={`funnel-activate-${c.id}`}
                                  className="flex-1 px-2 py-1 text-[10px] font-bold bg-amber-500 text-white rounded hover:bg-amber-600 flex items-center justify-center gap-1"
                                >
                                  <Check size={10} /> Aktivovať
                                </button>
                              ) : (
                                <span className="flex-1 px-2 py-1 text-[10px] font-bold bg-amber-100 text-amber-700 rounded text-center">LIVE</span>
                              )}
                              <button
                                onClick={() => copyAdText(c)}
                                className="px-2 py-1 text-[10px] bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                                title="Kopírovať"
                              >
                                {copyOk === c.id ? <Check size={10} className="text-green-600" /> : <Copy size={10} />}
                              </button>
                              <button
                                onClick={() => remove(c.id)}
                                className="px-2 py-1 text-[10px] bg-red-50 text-red-600 rounded hover:bg-red-100"
                                data-testid={`funnel-delete-${c.id}`}
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {canvasCreative && (
        <CanvasEditorModal
          creative={canvasCreative}
          onClose={() => setCanvasCreative(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, val, tint }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={16} style={{ color: tint }} />
      <div>
        <p className="text-[11px] text-gray-500 uppercase tracking-wide font-bold">{label}</p>
        <p className="text-base font-black text-gray-900 leading-tight">{val}</p>
      </div>
    </div>
  );
}

function ImageUrlInput({ initial, onSave, testId }) {
  const [val, setVal] = useState(initial || "");
  const dirty = val !== (initial || "");
  return (
    <div className="flex items-center gap-1 mt-2">
      <LinkIcon size={10} className="text-gray-400 flex-shrink-0" />
      <input
        type="url"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Image URL (Dropbox / imgbb / CDN)"
        data-testid={testId}
        className="flex-1 text-[10px] bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-amber-400"
      />
      {dirty && (
        <button
          onClick={() => onSave(val)}
          className="px-1.5 py-0.5 text-[9px] font-bold bg-amber-500 text-white rounded hover:bg-amber-600"
          data-testid={`${testId}-save`}
        >
          Uložiť
        </button>
      )}
    </div>
  );
}

// Per-funnel admin settings for the VSL video gate: embeddable video URL
// (YouTube / Vimeo / Loom / Wistia) + cta_delay_sec (how many seconds the
// checkout CTA stays hidden after unlock — classic VSL pattern).
function FunnelVideoSettings({ slug }) {
  const [cfg, setCfg] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [delaySec, setDelaySec] = useState(45);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    axios.get(`${API}/funnel/config/${slug}`)
      .then((r) => {
        setCfg(r.data);
        setVideoUrl(r.data.video_url || "");
        setDelaySec(Number(r.data.cta_delay_sec ?? 45));
      })
      .catch(() => {});
  }, [slug]);

  async function save() {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/funnel/admin/config/${slug}`,
        { video_url: videoUrl, cta_delay_sec: Number(delaySec) },
        { headers: adminHeaders() },
      );
      setCfg(data.funnel);
      setSavedAt(Date.now());
    } catch (e) {
      window.alert(`Uloženie zlyhalo: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  }

  if (!cfg) return null;
  const dirty = (cfg.video_url || "") !== videoUrl || Number(cfg.cta_delay_sec ?? 45) !== Number(delaySec);

  return (
    <div
      className="mt-3 pt-3 border-t border-gray-100"
      data-testid={`funnel-video-settings-${slug}`}
    >
      <p className="text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
        <PlayCircle size={10} /> Video gate
      </p>
      <div className="flex items-center gap-1.5">
        <input
          type="url"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          placeholder="YouTube / Vimeo / Loom / Wistia URL"
          data-testid={`funnel-video-url-${slug}`}
          className="flex-1 text-[11px] bg-gray-50 border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-amber-400"
        />
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">CTA delay</label>
        <input
          type="number"
          min="0" max="600" step="5"
          value={delaySec}
          onChange={(e) => setDelaySec(e.target.value)}
          data-testid={`funnel-cta-delay-${slug}`}
          className="w-16 text-[11px] font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-amber-400"
        />
        <span className="text-[10px] text-gray-500">s</span>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid={`funnel-video-save-${slug}`}
          className="ml-auto flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Uložiť
        </button>
      </div>
      {savedAt > 0 && Date.now() - savedAt < 2500 && (
        <p className="text-[10px] text-emerald-600 mt-1 font-bold">✓ Uložené</p>
      )}
    </div>
  );
}

